import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { IMAGE_TYPE } from '@ux/components/image/constants/image-type.constants';
import { Room } from '@booking/entities/hotel-booking.response';
import { totalNights, nightsLabel } from '@ux/mappers/guest.mapper';
import { Travelers } from '@booking/entities/booking-arrangement';
import { PaxType } from '@booking/entities/pax-type';
import { formatStrinWithTimeZoneDateByRegex } from '@ux/mappers/date.mapper';
import { HotelProvider } from '@hotels/hotel-results/entities/hotel-searched';
import { TagProductType } from '@design-system/components/organisms/tag-product/entities/tag-product.types';
import { TagBrandingType } from '@design-system/components/organisms/tag-product/entities/tag-branding.types';
import { isMarriottProvider } from '@ux/helpers/hotel-provider.helper';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-hotel-reservation',
  templateUrl: './hotel-reservation.component.html',
  styleUrls: ['./hotel-reservation.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelReservationComponent {

  @Input() image: string;
  @Input() hotelName: string;
  @Input() stars: number;
  @Input() destinationName: string;
  @Input() location: string;
  @Input() checkin: string;
  @Input() checkout: string;
  @Input() rooms: Room[];
  @Input() passengers: Travelers[] = [];
  @Input() isUserHistory: boolean = false;
  @Input() provider: HotelProvider;
  @Input() isByhours = false;
  @Input() packHours: number;

  public typeImageHotel: string = IMAGE_TYPE.HOTEL;
  public tagProductType = TagProductType.Informative;
  public brandingTagProduct = TagBrandingType.Marriott;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  constructor(
    public translate: TranslocoService,
  ) { }

  get hasCategory(): boolean {
    return !!this.stars;
  }

  get checkinDate(): Date {
    return new Date(formatStrinWithTimeZoneDateByRegex(this.checkin));
  }

  get checkoutDate(): Date {
    return new Date(formatStrinWithTimeZoneDateByRegex(this.checkout));
  }

  get totalNightsNumber(): number {
    return totalNights(this.checkout, this.checkin);
  }

  get isOneNight(): string {
    return nightsLabel(this.totalNightsNumber);
  }

  get adults(): number {
    return this.isUserHistory ?
      this.getPassengersByType(PaxType.ADULTS) :
      this.adultsByRooms;
  }

  get childrens(): number {
    return this.isUserHistory ?
      this.getPassengersByType(PaxType.CHILDREN) :
      this.childrensByRooms;
  }

  get infants(): number {
    return this.getPassengersByType(PaxType.INFANTS);
  }

  get getAdultsTranslate(): string {
    return this.adults > 1 ? 'MANY_ADULTS' : 'ADULT';
  }

  get getChildrenTranslate(): string {
    return this.childrens > 1 ? 'MANY_CHILDRENS' : 'CHILD';
  }

  get getInfantTranslate(): string {
    return this.infants > 1 ? 'MANY_INFANTS' : 'INFANT';
  }

  get isMarriottProvider(): boolean {
    return isMarriottProvider(this.provider);
  }

  private get adultsByRooms(): number {
    let quantity = 0;
    this.rooms.map(
      item => quantity += item.Adults
    );
    return quantity;
  }

  private get childrensByRooms(): number {
    let quantity = 0;
    this.rooms.map(
      item => quantity += item.Children
    );
    return quantity;
  }

  public getTranslate(key: string | string[]): string | string[] {
    return this.translate.translate(key);
  }

  public getPassengersByType(type: string): number {
    return this.passengers.filter(passenger => passenger.PaxType === type).length;
  }

}
